import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, isEmailVerified } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50"
        >
          <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-charcoal mb-2">Checking Authentication</h2>
          <p className="text-slate-gray">Please wait...</p>
        </motion.div>
      </div>
    );
  }

  if (!user) {
    // Send straight to the login form (not the bare homepage), carrying the
    // originally requested destination so it can be restored after login
    // instead of dropping the user on the homepage (client-reported issue #5-2).
    return <Navigate to="/startup-signup?mode=login" replace state={{ from: location }} />;
  }

  // Check if user's email is verified
  if (!isEmailVerified) {
    return <Navigate to="/verify-email" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;